'use client';

import { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';

// Coordinates the homepage's big search bar with the compact header search.
// On the homepage, the header search should only appear once the big search
// bar has scrolled out of view, and both must share the same query value.
//
// The query value lives here (single source of truth) so the header search and
// the main search bar are literally the same controlled input value — typing in
// one shows up in the other with no lost keystrokes.
const HomeSearchContext = createContext(null);

export function HomeSearchProvider({ children }) {
  // Whether the homepage's main search bar is currently on screen.
  // Starts true so the header search stays hidden until the homepage says otherwise.
  const [mainSearchVisible, setMainSearchVisible] = useState(true);

  // Shared query value + the homepage's committer (debounced URL sync).
  const [value, setValue] = useState('');
  const commitRef = useRef(null);

  // Called by whichever input the user types in. Updates the shared value
  // immediately and forwards to the homepage's committer (URL/debounce).
  const setQuery = useCallback((next) => {
    setValue(next);
    if (commitRef.current) commitRef.current(next);
  }, []);

  // The homepage registers its committer + keeps the shared value in sync with
  // the URL (back button, badge clicks). Called on every homepage render.
  const registerHomeSearch = useCallback((currentValue, commit) => {
    commitRef.current = commit;
    setValue(prev => (prev === currentValue ? prev : currentValue));
  }, []);

  const unregisterHomeSearch = useCallback(() => {
    commitRef.current = null;
    setMainSearchVisible(true);
    setValue('');
  }, []);

  // Stable context value so consumers only re-render when the data changes,
  // not on every provider render (setMainSearchVisible fires on scroll)
  const contextValue = useMemo(() => ({
    mainSearchVisible, setMainSearchVisible,
    value, setQuery,
    registerHomeSearch, unregisterHomeSearch,
  }), [mainSearchVisible, value, setQuery, registerHomeSearch, unregisterHomeSearch]);

  return (
    <HomeSearchContext.Provider value={contextValue}>
      {children}
    </HomeSearchContext.Provider>
  );
}

export function useHomeSearch() {
  return useContext(HomeSearchContext);
}
